import { buildAttachableTreeId, parseAttachableTreeId } from './utils/attachableTreeIds';
import { attachableInfoForDragTarget, ATTACHABLE_TYPE_FOR_CHILD_OF_COMPONENT } from './utils/componentCompatibility';
// Local recursive lookup (duplicated from utils/addComponentLaunchContext's
// findComponentRecursively, not imported, to avoid a circular import: that module
// itself imports parent-option mappers from this file).
function findComponentById(componentsArray, targetId) {
    const directMatch = componentsArray.find((component) => component.id === targetId);
    if (directMatch)
        return directMatch;
    const foundInChildren = componentsArray
        .filter((component) => component.children && component.children.length > 0)
        .map((component) => findComponentById(component.children, targetId))
        .find((result) => result !== null);
    return foundInChildren || null;
}
// --- Parent option helpers ---
/**
 * Builds a stable string key that uniquely identifies a ParentOption within a session.
 * Used as the value for SelectInput options so we can look up the full ParentOption on selection.
 */
export function buildParentOptionKey(option) {
    if (option.kind === 'attachable') {
        return `attachable:${option.attachableType}:${option.attachableId}`;
    }
    return `component:${option.componentId}`;
}
/**
 * Builds an AttachableParentOption from a ComponentAttachable API object.
 */
export function mapAttachableToParentOption(attachable) {
    var _a;
    return {
        kind: 'attachable',
        attachableId: attachable.attachableId,
        attachableType: attachable.attachableType,
        label: attachable.attachableDisplayName,
        subLabel: (_a = attachable.attachableHumanReadableType) !== null && _a !== void 0 ? _a : undefined
    };
}
/**
 * Builds a ComponentParentOption from a Component, carrying the component's attachable context.
 * The attachable context is required so that a child create can still send the correct
 * attachable_id / attachable_type to the API.
 */
export function mapComponentToParentOption(component) {
    var _a;
    return {
        kind: 'component',
        componentId: component.id,
        componentType: component.type,
        attachableId: component.attachableId,
        attachableType: component.attachableType,
        label: component.name,
        subLabel: (_a = component.attachableDisplayName) !== null && _a !== void 0 ? _a : undefined
    };
}
// Transform API component type to frontend format
export function mapComponentTypeToFrontend(type) {
    var _a;
    return {
        id: type.id,
        typeName: type.typeName,
        category: type.category,
        fieldDefinitions: type.fieldDefinitions,
        allowedAttachableTypes: type.allowedAttachableTypes,
        compatibleChildTypeIds: (_a = type.compatibleChildTypeIds) !== null && _a !== void 0 ? _a : []
    };
}
// Transform complete API response to frontend format
export function mapComponentsResponseToFrontend(response) {
    var _a;
    return {
        components: response.components,
        componentTypes: response.componentTypes.map(mapComponentTypeToFrontend),
        componentAttachables: response.componentAttachables,
        parentComponentAttachables: (_a = response.parentComponentAttachables) !== null && _a !== void 0 ? _a : response.componentAttachables
    };
}
// Resolve the attachable info (id + type) for a drag-and-drop tree item — either an
// attachable-root pseudo-id, or an existing Component (using its OWN current attachable context).
function findAttachableInfoForTreeItem(itemId, components) {
    const parsedAttachable = parseAttachableTreeId(itemId);
    if (parsedAttachable)
        return parsedAttachable;
    if (String(itemId).startsWith('attachable-'))
        return null;
    const component = findComponentById(components, Number(itemId));
    return component ? { attachableId: component.attachableId, attachableType: component.attachableType } : null;
}
function findNewParentAttachableInfo(newParentId, components) {
    if (newParentId == null)
        return null;
    const parsedAttachable = parseAttachableTreeId(newParentId);
    if (parsedAttachable)
        return parsedAttachable;
    const parentComponent = findComponentById(components, Number(newParentId));
    return parentComponent ? attachableInfoForDragTarget(parentComponent) : null;
}
function collectComponentIdsRecursively(components, componentId) {
    const component = findComponentById(components, componentId);
    if (!component)
        return [];
    const ids = [componentId];
    const collectChildIds = (children) => {
        const childIds = [];
        children.forEach((child) => {
            childIds.push(child.id);
            if (child.children && child.children.length > 0) {
                childIds.push(...collectChildIds(child.children));
            }
        });
        return childIds;
    };
    if (component.children && component.children.length > 0) {
        ids.push(...collectChildIds(component.children));
    }
    return ids;
}
export function buildReorderResultWithAttachableUpdates(result, components) {
    var _a, _b, _c, _d;
    const currentAttachableInfo = findAttachableInfoForTreeItem(result.draggedItem.id, components);
    const newAttachableInfo = findNewParentAttachableInfo(result.newParentId, components);
    const currentAttachableId = (_a = currentAttachableInfo === null || currentAttachableInfo === void 0 ? void 0 : currentAttachableInfo.attachableId) !== null && _a !== void 0 ? _a : null;
    const currentAttachableType = (_b = currentAttachableInfo === null || currentAttachableInfo === void 0 ? void 0 : currentAttachableInfo.attachableType) !== null && _b !== void 0 ? _b : null;
    const newAttachableId = (_c = newAttachableInfo === null || newAttachableInfo === void 0 ? void 0 : newAttachableInfo.attachableId) !== null && _c !== void 0 ? _c : null;
    const newAttachableType = (_d = newAttachableInfo === null || newAttachableInfo === void 0 ? void 0 : newAttachableInfo.attachableType) !== null && _d !== void 0 ? _d : null;
    let uiReorderResult = {
        draggedItem: {
            id: result.draggedItem.id,
            parentId: result.draggedItem.parentId,
            position: result.draggedItem.position
        },
        newParentId: result.newParentId,
        newPosition: result.newPosition,
        positionUpdates: result.positionUpdates.map((update) => ({
            id: update.id,
            parentId: update.parentId,
            position: update.position
        }))
    };
    const attachableChanged = (currentAttachableId !== newAttachableId || currentAttachableType !== newAttachableType) &&
        newAttachableId !== null &&
        newAttachableType !== null;
    if (!attachableChanged)
        return uiReorderResult;
    const affectedComponentIds = collectComponentIdsRecursively(components, Number(result.draggedItem.id));
    const draggedComponentId = Number(result.draggedItem.id);
    const droppedOntoComponent = parseAttachableTreeId(result.newParentId) === null;
    const attachableUpdates = affectedComponentIds
        .map((componentId) => {
        const existingUpdate = uiReorderResult.positionUpdates.find((u) => Number(u.id) === componentId);
        // Dropped onto a Component: the dragged item carries parent_id, and the server derives
        // its attachable from the chosen ComponentType. Stamping a guess here is what broke
        // ordinary subcomponents, whose type mirrors the parent's resource rather than nesting.
        if (componentId === draggedComponentId && droppedOntoComponent) {
            return existingUpdate !== null && existingUpdate !== void 0 ? existingUpdate : null;
        }
        // A nested-only descendant's attachable already points at its own parent, which this
        // move does not change.
        if (componentId !== draggedComponentId) {
            const descendant = findComponentById(components, componentId);
            if ((descendant === null || descendant === void 0 ? void 0 : descendant.attachableType) === ATTACHABLE_TYPE_FOR_CHILD_OF_COMPONENT)
                return existingUpdate !== null && existingUpdate !== void 0 ? existingUpdate : null;
        }
        if (existingUpdate) {
            return Object.assign(Object.assign({}, existingUpdate), { attachableId: newAttachableId, attachableType: newAttachableType });
        }
        if (componentId !== draggedComponentId) {
            // Child components: only send attachable_id/attachable_type, no position/parent changes,
            // to preserve existing values in optimistic updates.
            return { id: componentId, attachableId: newAttachableId, attachableType: newAttachableType };
        }
        const component = findComponentById(components, componentId);
        return {
            id: componentId,
            parentId: (component === null || component === void 0 ? void 0 : component.parentId) || null,
            position: (component === null || component === void 0 ? void 0 : component.position) || 0,
            attachableId: newAttachableId,
            attachableType: newAttachableType
        };
    })
        .filter((update) => update !== null);
    uiReorderResult = Object.assign(Object.assign({}, uiReorderResult), { positionUpdates: [
            ...uiReorderResult.positionUpdates.filter((u) => !affectedComponentIds.includes(Number(u.id))),
            ...attachableUpdates
        ] });
    return uiReorderResult;
}
// Transform drag-drop result to API reorder request
export function mapDragDropResultToReorderRequest(result) {
    return {
        position_updates: result.positionUpdates.map((update) => {
            var _a, _b;
            const apiUpdate = {
                id: Number(update.id)
            };
            // Only add parent_id if the property exists in the update (not AttachableOnlyUpdate)
            if ('parentId' in update && update.parentId !== undefined) {
                const attachableParent = parseAttachableTreeId(update.parentId);
                if (attachableParent) {
                    apiUpdate.parent_id = null;
                    apiUpdate.attachable_id = (_a = update.attachableId) !== null && _a !== void 0 ? _a : attachableParent.attachableId;
                    apiUpdate.attachable_type = (_b = update.attachableType) !== null && _b !== void 0 ? _b : attachableParent.attachableType;
                }
                else {
                    apiUpdate.parent_id = update.parentId ? Number(update.parentId) : null;
                }
            }
            // Only add position if the property exists in the update (not AttachableOnlyUpdate)
            if ('position' in update && update.position !== undefined) {
                apiUpdate.position = update.position;
            }
            // Only add attachable_id if it's defined
            if (update.attachableId !== undefined) {
                apiUpdate.attachable_id = update.attachableId;
            }
            // Only add attachable_type if it's defined
            if (update.attachableType !== undefined) {
                apiUpdate.attachable_type = update.attachableType;
            }
            return apiUpdate;
        })
    };
}
// Transform frontend components to HierarchicalTree TreeItems
export function mapComponentsToTreeItems(components) {
    return components.map((component) => ({
        id: component.id,
        parentId: component.parentId,
        position: component.position,
        children: component.children ? mapComponentsToTreeItems(component.children) : [],
        data: {
            id: component.id,
            name: component.name,
            type: component.type,
            typeId: component.typeId,
            attachableDisplayName: component.attachableDisplayName,
            attachableHumanReadableType: component.attachableHumanReadableType,
            location: component.location,
            attachableType: component.attachableType,
            attachableId: component.attachableId,
            depth: component.depth,
            barcode: component.barcode,
            parentId: component.parentId,
            active: component.active
        }
    }));
}
// Group components by attachableId and create a hierarchical structure
// Uses componentAttachables as the source of truth for all attachables
export function mapComponentsToGroupedTreeItems(components, componentAttachables) {
    // Create a map of components by attachable key for quick lookup
    const componentsByAttachable = components.reduce((map, component) => {
        const key = `${component.attachableType}-${component.attachableId}`;
        if (!map[key]) {
            map[key] = [];
        }
        map[key].push(component);
        return map;
    }, {});
    // Convert attachables to TreeItems
    return componentAttachables
        .map((attachable, index) => {
        const key = `${attachable.attachableType}-${attachable.attachableId}`;
        const attachableComponents = componentsByAttachable[key] || [];
        // Create attachable root item
        const attachableId = buildAttachableTreeId(attachable.attachableType, attachable.attachableId);
        return {
            id: attachableId,
            parentId: null,
            position: index,
            children: mapComponentsToTreeItems(attachableComponents),
            isDragDisabled: true, // Disable dragging for attachable root items
            data: {
                name: attachable.attachableDisplayName,
                attachableType: attachable.attachableType,
                attachableId: attachable.attachableId,
                displayName: attachable.attachableDisplayName,
                humanReadableType: attachable.attachableHumanReadableType,
                location: attachable.attachableLocation
            }
        };
    })
        .sort((a, b) => a.data.name.localeCompare(b.data.name));
}
// Create empty component for adding new ones
export function createEmptyComponent(parentId, position) {
    return {
        name: '',
        location: '',
        parentId: parentId || null,
        position: position || 0,
        active: true
    };
}
// Map form data to API request format for creating component.
// selectedParent drives all three API fields: attachable_id, attachable_type, parent_component_id.
export function mapCreateComponentFormToRequest(formData) {
    /* eslint-disable @typescript-eslint/naming-convention */
    const { selectedParent } = formData;
    if (!selectedParent) {
        throw new Error('mapCreateComponentFormToRequest: selectedParent is required');
    }
    // An attachable parent is a root component: its attachable is the selected resource itself.
    // A component parent sends parent_component_id alone and lets the server derive the
    // attachable — ExternalApi::Components::AttachableDerivation branches on the chosen
    // ComponentType, which is what decides between the nested-only "Component" marker and
    // mirroring the parent's own resource. Sending a guess here cannot make that call.
    const requestData = selectedParent.kind === 'component'
        ? {
            name: formData.name,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            component_type_id: formData.typeId,
            parent_component_id: selectedParent.componentId,
            location: formData.location || undefined,
            barcode: formData.barcode || undefined,
            external_id: formData.externalId || undefined,
            notes: formData.notes || undefined
        }
        : {
            name: formData.name,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            component_type_id: formData.typeId,
            attachable_id: selectedParent.attachableId,
            attachable_type: selectedParent.attachableType,
            parent_component_id: null,
            location: formData.location || undefined,
            barcode: formData.barcode || undefined,
            external_id: formData.externalId || undefined,
            notes: formData.notes || undefined
        };
    if (formData.settings && Object.keys(formData.settings).length > 0) {
        requestData.settings = formData.settings;
    }
    return requestData;
    /* eslint-enable @typescript-eslint/naming-convention */
}
// Map frontend update data to API request format
export function mapUpdateDataToRequest(updateData) {
    const requestData = {};
    // Map common field names to API field names
    Object.entries(updateData).forEach(([field, value]) => {
        switch (field) {
            case 'name':
                requestData.name = value;
                break;
            case 'description':
                requestData.description = value;
                break;
            case 'typeId':
                requestData.component_type_id = value;
                break;
            case 'location':
                requestData.location = value;
                break;
            case 'barcode':
                requestData.barcode = value;
                break;
            case 'externalId':
                requestData.external_id = value;
                break;
            case 'notes':
                requestData.notes = value;
                break;
            case 'settings':
                requestData.settings = value;
                break;
            case 'parentId':
                requestData.parent_component_id = value;
                break;
            default:
                // For any other fields, just pass through
                requestData[field] = value;
                break;
        }
    });
    return requestData;
}
export default {
    buildParentOptionKey,
    mapAttachableToParentOption,
    mapComponentToParentOption,
    mapComponentTypeToFrontend,
    mapComponentsResponseToFrontend,
    buildReorderResultWithAttachableUpdates,
    mapDragDropResultToReorderRequest,
    mapComponentsToTreeItems,
    mapComponentsToGroupedTreeItems,
    createEmptyComponent,
    mapCreateComponentFormToRequest,
    mapUpdateDataToRequest
};
